import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Input from '../Common/Input';
import Button from '../Common/Button';
import { validateTask } from '../../utils/validation';
import { useGoals } from '../../contexts/GoalContext';
import { logTaskCreated, logTaskEdited } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { useCalendars } from '../../contexts/CalendarContext';
import { getUserProfile } from '../../services/userService';

const TaskForm = ({ task, onSave, onCancel, initialDate }) => {
    const { goals } = useGoals();
    const { currentUser } = useAuth();
    const { selectedCalendar } = useCalendars();
    const [calendarMembers, setCalendarMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [currentUserProfile, setCurrentUserProfile] = useState(null);

    const [formData, setFormData] = useState({
        task: '',
        owner: null,
        dueDate: initialDate ? format(initialDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        acceptanceCriteria: '',
        dependencies: '',
        contingencyPlan: '',
        priority: 'medium',
        status: 'not-started',
        completed: false,
        completedAt: null,
        goalId: null
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch current user profile
    useEffect(() => {
        const fetchCurrentUserProfile = async () => {
            if (currentUser?.uid) {
                try {
                    const profile = await getUserProfile(currentUser.uid);
                    setCurrentUserProfile(profile);
                } catch (error) {
                    console.error('Error fetching current user profile:', error);
                    setCurrentUserProfile(null);
                }
            }
        };

        fetchCurrentUserProfile();
    }, [currentUser]);

    // Fetch calendar members
    useEffect(() => {
        const fetchMembers = async () => {
            if (!selectedCalendar || !selectedCalendar.members) {
                setCalendarMembers([]);
                setLoadingMembers(false);
                return;
            }

            try {
                setLoadingMembers(true);
                const memberPromises = selectedCalendar.members.map(async (memberId) => {
                    try {
                        const profile = await getUserProfile(memberId);
                        return {
                            id: memberId,
                            displayName: profile?.displayName || profile?.email || memberId,
                            email: profile?.email || '',
                            permission: selectedCalendar.permissions?.[memberId] || 'viewer'
                        };
                    } catch (error) {
                        console.error(`Error fetching profile for member ${memberId}:`, error);
                        return {
                            id: memberId,
                            displayName: memberId,
                            email: '',
                            permission: selectedCalendar.permissions?.[memberId] || 'viewer'
                        };
                    }
                });

                const members = await Promise.all(memberPromises);
                setCalendarMembers(members);
            } catch (error) {
                console.error('Error fetching calendar members:', error);
                setCalendarMembers([]);
            } finally {
                setLoadingMembers(false);
            }
        };

        fetchMembers();
    }, [selectedCalendar]);

    useEffect(() => {
        if (task) {
            // Editing existing task - use task's data
            // Ensure goalId is null if it's empty string or falsy
            const goalId = task.goalId && task.goalId !== '' ? task.goalId : null;

            // Handle owner - could be string (legacy) or object
            let owner = task.owner;
            if (typeof owner === 'string') {
                // Legacy format - convert to object
                owner = { id: owner, name: owner }; // Fallback to ID as name
            }

            setFormData({
                task: task.task || '',
                owner: owner,
                dueDate: task.dueDate || format(new Date(), 'yyyy-MM-dd'),
                acceptanceCriteria: task.acceptanceCriteria || '',
                dependencies: task.dependencies || '',
                contingencyPlan: task.contingencyPlan || '',
                priority: task.priority || 'medium',
                status: task.status || 'not-started',
                completed: task.completed || false,
                completedAt: task.completedAt || null,
                goalId: goalId
            });
        } else {
            // Creating new task - set current user as default owner
            if (currentUserProfile) {
                setFormData(prev => ({
                    ...prev,
                    owner: {
                        id: currentUser.uid,
                        name: currentUserProfile.displayName || currentUserProfile.email || currentUser.uid
                    }
                }));
            }
        }
    }, [task, currentUser, currentUserProfile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Convert empty string to null for goalId
        const finalValue = (name === 'goalId' && value === '') ? null : value;
        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const validationErrors = validateTask(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            await onSave(formData);
            if (task) {
                logTaskEdited({ priority: formData.priority, has_goal: !!formData.goalId });
            } else {
                logTaskCreated({ priority: formData.priority, status: formData.status, has_goal: !!formData.goalId });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrors({ submit: 'Failed to save task. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <Input
                label="Task Name"
                id="task"
                name="task"
                value={formData.task}
                onChange={handleChange}
                placeholder="Enter task name"
                required
                error={errors.task}
            />

            <div className="mb-3 sm:mb-4">
                <label htmlFor="owner" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Owner <span className="text-red-500">*</span>
                </label>
                <select
                    id="owner"
                    name="owner"
                    value={formData.owner?.id || ''}
                    onChange={(e) => {
                        const selectedMember = calendarMembers.find(m => m.id === e.target.value);
                        if (selectedMember) {
                            setFormData(prev => ({
                                ...prev,
                                owner: {
                                    id: selectedMember.id,
                                    name: selectedMember.displayName
                                }
                            }));
                        }
                    }}
                    required
                    disabled={loadingMembers}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 touch-manipulation"
                >
                    <option value="">{loadingMembers ? 'Loading members...' : 'Select an owner'}</option>
                    {calendarMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                            {member.displayName} {member.permission !== 'viewer' ? '' : '(Viewer)'}
                        </option>
                    ))}
                </select>
                {errors.owner && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400" role="alert">{errors.owner}</p>
                )}
            </div>

            <Input
                label="Due Date"
                id="dueDate"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={handleChange}
                required
                error={errors.dueDate}
            />

            <div className="mb-3 sm:mb-4">
                <label htmlFor="priority" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority
                </label>
                <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 touch-manipulation"
                >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                </select>
            </div>

            <div className="mb-3 sm:mb-4">
                <label htmlFor="status" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                </label>
                <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 touch-manipulation"
                >
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                </select>
            </div>

            <div className="mb-3 sm:mb-4">
                <label htmlFor="acceptanceCriteria" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Acceptance Criteria
                </label>
                <textarea
                    id="acceptanceCriteria"
                    name="acceptanceCriteria"
                    value={formData.acceptanceCriteria}
                    onChange={handleChange}
                    placeholder="Enter acceptance criteria"
                    rows={2}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 sm:rows-3"
                />
                {errors.acceptanceCriteria && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400" role="alert">{errors.acceptanceCriteria}</p>
                )}
            </div>

            <div className="mb-3 sm:mb-4">
                <label htmlFor="dependencies" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Dependencies
                </label>
                <textarea
                    id="dependencies"
                    name="dependencies"
                    value={formData.dependencies}
                    onChange={handleChange}
                    placeholder="Enter task dependencies"
                    rows={2}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
            </div>

            <div className="mb-3 sm:mb-4">
                <label htmlFor="goalId" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Associated Goal (Optional)
                </label>
                <select
                    id="goalId"
                    name="goalId"
                    value={formData.goalId || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 touch-manipulation"
                >
                    <option value="">None</option>
                    {goals.filter(g => g.status === 'active').map(goal => (
                        <option key={goal.id} value={goal.id}>
                            {goal.title} ({goal.period})
                        </option>
                    ))}
                </select>
            </div>

            <div className="mb-3 sm:mb-4">
                <label htmlFor="contingencyPlan" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contingency Plan
                </label>
                <textarea
                    id="contingencyPlan"
                    name="contingencyPlan"
                    value={formData.contingencyPlan}
                    onChange={handleChange}
                    placeholder="Enter contingency plan"
                    rows={2}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
            </div>

            {errors.submit && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">{errors.submit}</p>
                </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                >
                    {isSubmitting ? 'Saving...' : 'Save Task'}
                </Button>
            </div>
        </form>
    );
};

export default TaskForm;